import { ActionType, ComponentType, logEvent } from '@cbhq/client-analytics';
import type { Target } from '@cbhq/identity-shared/types';

import { fingerprintStore, mapTargetToFingerprintProSealedApiKey } from '../store';
import { PACKAGE_VERSION } from '../version';

import { dynamicImportAgent } from './dynamicImportAgent';

/**
 * Custom error class for SDK loading failures.
 * Carries context about the loading method and whether fallback was triggered.
 */
export class SDKLoadError extends Error {
  public loadMethod: 'dynamic_import' | 'html_preload';

  public fallbackTriggered: boolean;

  public originalError?: Error;

  constructor(
    message: string,
    loadMethod: 'dynamic_import' | 'html_preload',
    fallbackTriggered = false,
    originalError?: Error,
  ) {
    super(message);
    this.name = 'SDKLoadError';
    this.loadMethod = loadMethod;
    this.fallbackTriggered = fallbackTriggered;
    this.originalError = originalError;
    // Preserve original stack trace if available
    if (originalError?.stack) {
      this.stack = originalError.stack;
    }
  }
}

// Global promise to hold the FingerprintJS agent
let fpAgentPromise: Promise<FingerprintAgent> | undefined;

// Prevents duplicate promise chains when multiple components call initAgent()
let htmlPreloadAttempted = false;

/**
 * Initialize the FingerprintJS Pro agent
 * Three paths:
 * 1. Already initialized → return
 * 2. HTML preload available → use with fallback
 * 3. No preload → dynamic import
 *
 * @param target - The target environment (production, sandbox, development)
 */
export function initAgent(target: Target = 'production'): void {
  // PATH 1: Already initialized
  if (fpAgentPromise) {
    return;
  }

  // Fallback timing - only used if HTML preload timing unavailable
  const sdkLoadStartTime = performance.now();

  // Log that SDK loading has been initiated (fires ONCE per initAgent call)
  logEvent('identity_fingerprint_pro_sdk_init_started', {
    action: ActionType.api,
    componentType: ComponentType.api,
    sealed: true,
    version: PACKAGE_VERSION,
  });

  // PATH 2: HTML preload available (one-time check)
  if (typeof window !== 'undefined' && window.fpAgentPromise && !htmlPreloadAttempted) {
    htmlPreloadAttempted = true;

    fpAgentPromise = window.fpAgentPromise
      .then((agent) => {
        if (agent === null) {
          // HTML preload failed, fall back to dynamic import
          console.warn('HTML preload failed, falling back to dynamic import');

          const apiKey = mapTargetToFingerprintProSealedApiKey[target];
          const fallbackStartTime = performance.now();
          return dynamicImportAgent(apiKey)
            .then((fallbackAgent) => {
              // Track SDK load time for fallback path
              if (!fingerprintStore.timing.sdkLoadTime) {
                fingerprintStore.timing.sdkLoadTime = performance.now() - fallbackStartTime;
              }

              // Log successful SDK load via fallback after HTML preload returned null
              logEvent('identity_fingerprint_pro_sdk_init_completed', {
                action: ActionType.api,
                componentType: ComponentType.api,
                load_method: 'dynamic_import',
                was_fallback: true,
                perf_sdk_load_ms: Math.round(fingerprintStore.timing.sdkLoadTime),
                sealed: true,
                version: PACKAGE_VERSION,
              });

              return fallbackAgent;
            })
            .catch((fallbackError) => {
              // If fallback also fails, throw SDKLoadError with full context
              const errorMessage =
                window.fpAgentError?.message ??
                fallbackError?.message ??
                'HTML preload failed and dynamic import failed';

              throw new SDKLoadError(errorMessage, 'html_preload', true, fallbackError);
            });
        }
        // HTML preload succeeded - track load time
        if (!fingerprintStore.timing.sdkLoadTime) {
          // window.fpAgentLoadTime: actual SDK load time from HTML page start
          // sdkLoadStartTime fallback: time from initAgent() call (less accurate)
          fingerprintStore.timing.sdkLoadTime =
            window.fpAgentLoadTime ?? performance.now() - sdkLoadStartTime;
        }

        // Log successful SDK load via HTML preload (primary path)
        logEvent('identity_fingerprint_pro_sdk_init_completed', {
          action: ActionType.api,
          componentType: ComponentType.api,
          load_method: 'html_preload',
          was_fallback: false,
          perf_sdk_load_ms: Math.round(fingerprintStore.timing.sdkLoadTime),
          sealed: true,
          version: PACKAGE_VERSION,
        });

        return agent;
      })
      .catch((error) => {
        // HTML promise rejected, fall back to dynamic import
        console.warn('HTML preload rejected, falling back to dynamic import', error);

        const apiKey = mapTargetToFingerprintProSealedApiKey[target];
        const fallbackStartTime = performance.now();
        return dynamicImportAgent(apiKey)
          .then((fallbackAgent) => {
            // Track SDK load time for fallback path
            if (!fingerprintStore.timing.sdkLoadTime) {
              fingerprintStore.timing.sdkLoadTime = performance.now() - fallbackStartTime;
            }

            // Log successful SDK load via fallback after HTML promise rejected
            logEvent('identity_fingerprint_pro_sdk_init_completed', {
              action: ActionType.api,
              componentType: ComponentType.api,
              load_method: 'dynamic_import',
              was_fallback: true,
              perf_sdk_load_ms: Math.round(fingerprintStore.timing.sdkLoadTime),
              sealed: true,
              version: PACKAGE_VERSION,
            });

            return fallbackAgent;
          })
          .catch((fallbackError) => {
            // If fallback also fails, throw SDKLoadError with full context
            const errorMessage =
              error?.message ??
              fallbackError?.message ??
              'HTML preload rejected and dynamic import failed';

            throw new SDKLoadError(errorMessage, 'html_preload', true, fallbackError ?? error);
          });
      });
    return;
  }

  // PATH 3: Dynamic import with retry support
  const apiKey = mapTargetToFingerprintProSealedApiKey[target];
  fpAgentPromise = dynamicImportAgent(apiKey)
    .then((agent) => {
      // Track SDK load time for dynamic import path
      if (!fingerprintStore.timing.sdkLoadTime) {
        // For dynamic import, we measure from initAgent() call
        fingerprintStore.timing.sdkLoadTime = performance.now() - sdkLoadStartTime;
      }

      // Log successful SDK load via direct dynamic import (no HTML preload)
      logEvent('identity_fingerprint_pro_sdk_init_completed', {
        action: ActionType.api,
        componentType: ComponentType.api,
        load_method: 'dynamic_import',
        was_fallback: false,
        perf_sdk_load_ms: Math.round(fingerprintStore.timing.sdkLoadTime),
        sealed: true,
        version: PACKAGE_VERSION,
      });

      return agent;
    })
    .catch((error) => {
      // Clear promise to allow retry on next mount
      fpAgentPromise = undefined;

      // Throw SDKLoadError with context
      throw new SDKLoadError(
        error?.message ?? 'Dynamic import failed',
        'dynamic_import',
        false,
        error,
      );
    });
}

/**
 * Get the initialized FingerprintJS Pro agent
 * Self-healing: automatically initializes if not already done
 * @returns Promise that resolves to the FingerprintJS agent
 */
export async function getAgent() {
  if (!fpAgentPromise) {
    // Use target from store or default to 'production'
    const actualTarget = fingerprintStore.initConfig?.target;
    const target = actualTarget ?? 'production';

    // Log auto-recovery when getAgent() finds no fpAgentPromise (cold start)
    logEvent('identity_fingerprint_pro_init_recovery', {
      action: ActionType.api,
      componentType: ComponentType.api,
      target: actualTarget ?? 'fallback_production',
      has_init_config: !!fingerprintStore.initConfig,
      version: PACKAGE_VERSION,
    });

    // Auto-initialize the agent
    initAgent(target);

    // After initAgent, fpAgentPromise should always be defined
    // If not, something went wrong
    if (!fpAgentPromise) {
      throw new SDKLoadError(
        'Failed to initialize FingerprintJS Pro agent',
        'dynamic_import',
        false,
      );
    }
  }

  return fpAgentPromise;
}

// Internal function used for testing
// eslint-disable-next-line no-underscore-dangle
export function __INTERNAL__clearAgent() {
  fpAgentPromise = undefined;
  htmlPreloadAttempted = false;
}
